import {
  Download as DownloadIcon,
  KeyboardArrowDown as ArrowIcon,
} from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { STRONG_DROPDOWN_PAPER_SX } from "components/common/dropdownPaper";
import type { ConfigExportFormat } from "features/integrationsBeta/configExportFormats";
import { useState } from "react";

export type ConfigExportScope = "config" | "field";

/** Menu copy per format, worded for the whole config vs one table. */
const FORMAT_COPY: Record<
  ConfigExportScope,
  Record<ConfigExportFormat, { label: string; hint: string }>
> = {
  config: {
    xlsx: {
      label: "Excel",
      hint: "One sheet per table, plus Settings and About",
    },
    csv: { label: "CSV", hint: "One table at a time" },
    json: {
      label: "JSON",
      hint: "The stored configuration, exactly as saved",
    },
  },
  field: {
    xlsx: {
      label: "Excel",
      hint: "This table as a sheet - codes keep leading zeros",
    },
    csv: {
      label: "CSV",
      hint: "This table only - Excel may reformat IDs on open",
    },
    json: { label: "JSON", hint: "Rows exactly as stored" },
  },
};

interface Props {
  /** Whole config or one table: picks the hint under each format. */
  scope: ConfigExportScope;
  /** Formats the server offers for this target, in menu order (the `exports` catalog). */
  formats: ConfigExportFormat[];
  onExport: (format: ConfigExportFormat) => void;
  busy?: boolean;
  /** Caption above the choices - e.g. the unsaved-changes reminder. */
  note?: string;
  ariaLabel?: string;
}

/**
 * "Export" button + menu of the formats the server offers for a config or
 * one of its tables. Renders nothing when the catalog offers no format.
 */
export default function ConfigExportMenu({
  scope,
  formats,
  onExport,
  busy,
  note,
  ariaLabel,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  if (!formats.length) return null;
  const copy = FORMAT_COPY[scope];

  return (
    <>
      <Button
        size="small"
        startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
        endIcon={<ArrowIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={busy}
        aria-haspopup="menu"
        aria-label={ariaLabel}
        sx={{ textTransform: "none", flexShrink: 0 }}
      >
        Export
      </Button>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        slotProps={{
          paper: {
            elevation: 0,
            sx: [STRONG_DROPDOWN_PAPER_SX, { maxWidth: 360 }],
          },
        }}
      >
        {note && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, pt: 1, pb: 0.5, display: "block", maxWidth: 320 }}
          >
            {note}
          </Typography>
        )}
        {formats.map((format) => (
          <MenuItem
            key={format}
            onClick={() => {
              close();
              onExport(format);
            }}
          >
            <ListItemText
              primary={copy[format].label}
              secondary={copy[format].hint}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
